import React, { useState, useEffect, useCallback } from 'react';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import 'bootstrap-daterangepicker/daterangepicker.css';
import styled from 'styled-components';
import { get } from '../utils/get';
import moment from 'moment';
import { FieldProps } from 'formik';
import { TitleText } from './Text';

interface CustomInputProps {
  placeholder?: string;
  backgroundColor?: string;
  value?: string;
  [key: string]: any;
}

const CustomInput = ({
  placeholder,
  backgroundColor = '#fff',
  value,
  ...props
}: CustomInputProps) => (
  <InputContainer>
    <DatePickerInput
      backgroundColor={backgroundColor}
      value={value}
      placeholder={placeholder}
      {...props}
    />
  </InputContainer>
);

interface DateRangePicker2Props extends FieldProps {
  label?: string;
  backgroundColor?: string;
}

const DateRangePicker2 = ({
  label,
  backgroundColor,
  field: { value, name },
  form: { setFieldValue }
}: DateRangePicker2Props) => {
  const [ranges, setRanges] = useState({
    'YTD': [moment().startOf('year'), moment()],
    'Month': [moment().startOf('month'), moment().endOf('month')],
    'Quarter': [moment().startOf('quarter'), moment().endOf('quarter')],
    prvYear: [
      moment().subtract(1, 'year').startOf('year'),
      moment().subtract(1, 'year').endOf('year')
    ],
    thisYear: [
      moment().startOf('year'),
      moment().endOf('year')
    ]
  });

  const [active, setActive] = useState('thisYear');

  const handleFilters = useCallback((type: string) => {
    setActive(type);
    const [startDate, endDate] = filterMappings[type] || [];
    if (startDate && endDate) {
      setFieldValue(name, { startDate, endDate });
    }
  }, [setFieldValue, name]);

  useEffect(() => {
    handleFilters('thisYear');
  }, [handleFilters]);

  const filterMappings: Record<string, [moment.Moment, moment.Moment]> = {
    YTD: [moment().startOf('year'), moment()],
    Month: [moment().startOf('month'), moment().endOf('month')],
    Quarter: [moment().startOf('quarter'), moment().endOf('quarter')],
    prvYear: [
      moment().subtract(1, 'year').startOf('year'),
      moment().subtract(1, 'year').endOf('year')
    ],
    thisYear: [moment().startOf('year'), moment().endOf('year')]
  };

  return (
    <Container isLabelled={!!label}>
      {label && <DateLabel>{label}</DateLabel>}
      <DatesContainer>
        <DateRangePicker
          autoUpdateInput={false}
          startDate={get(value, 'startDate')}
          endDate={get(value, 'endDate')}
          applyButtonClasses='btn-primary'
          onApply={() => setActive('')}
          onEvent={(event, picker) => setFieldValue(name, {
            startDate: picker.startDate,
            endDate: picker.endDate
          })}
          ranges={ranges}
        >
          <DateRangeContainer>
            <CustomInput
              backgroundColor={backgroundColor}
              value={get(value, 'startDate')
                ? moment(get(value, 'startDate')).format('DD MMMM YYYY')
                : ''}
            />
            <Devider>-</Devider>
            <CustomInput
              backgroundColor={backgroundColor}
              value={get(value, 'endDate')
                ? moment(get(value, 'endDate')).format('DD MMMM YYYY')
                : ''}
            />
          </DateRangeContainer>
        </DateRangePicker>
        {Object.keys(filterMappings).map(type => (
          <TitleTextStyled
            key={type}
            style={active === type ? { borderBottom: '2px solid #01A8CE' } : {}}
            onClick={() => handleFilters(type)}
          >
            {type}
          </TitleTextStyled>
        ))}
      </DatesContainer>
    </Container>
  );
};

export default DateRangePicker2;

const TitleTextStyled = styled(TitleText)`
  font-size: 12px;
  padding: 0 1em;
  color: #01a8ce;
  cursor: pointer;
  height: 2.65em;
`;

const Container = styled.div<{ isLabelled: boolean }>`
  display: grid;
  grid-template-rows: ${props => props.isLabelled ? '1em auto' : 'auto'};
`;

const DateRangeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DatesContainer = styled.div`
  display: flex;
  ${TitleTextStyled} {
    font-size: 12px;
    padding: 0 1em;
    color: #01a8ce;
    cursor: pointer;
    height: 2.65em;

    &:focus {
      border-bottom: 2px solid #01a8ce;
    }
  }
`;

const InputContainer = styled.div`
  position: relative;
`;

const DatePickerInput = styled.input<{ backgroundColor: string }>`
  padding: 0.5em 1em;
  padding-left: 0em;
  margin: 0.5em 1em;
  font-weight: bold;
  font-size: 12px;
  background-color: ${props => props.backgroundColor || '#fff'};
  color: #214160;
  box-shadow: none;
  border: none;
  width: 10em;
  border-bottom: 1px solid rgba(33, 65, 96, 0.6);

  &:focus {
    border-bottom: 2px solid rgba(33, 65, 96, 0.6);
    outline: none;
  }

  ::placeholder {
    color: #999;
    font-size: 0.9rem;
  }
`;

const Devider = styled.span`
  text-align: center;
  align-self: center;
  font-weight: 500;
`;

const DateLabel = styled.label`
  color: rgba(33, 65, 96, 0.4);
  font-size: 0.75em;
`;
